/**
 * Shared value editor. Renders the right input for a field's `kind` and the chosen
 * operator. Extracted from <FilterBuilder> so it can be reused by the bulk-edit
 * dialog (where there is no operator chooser — a fixed 'eq'/'among' is passed in).
 *
 * The value is always kept as a STRING in the draft (comma-joined for multi-value),
 * so callers serialize it the same way FilterBuilder.toClause does.
 */

import * as React from "react";

import type { FilterOperator } from "@/api/client";
import { usePrimaries, useUiks } from "@/api/hooks";
import { CellTreeSelect } from "@/components/CellTreeSelect";
import { resolveOptions, type FieldDef, type FieldOption } from "@/lib/fields";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

export interface ValueEditorProps {
  def: FieldDef | undefined;
  operator: FilterOperator;
  value: string;
  options: ReturnType<typeof resolveOptions>;
  /** branch_id chosen elsewhere in the form (feeds primary_id / uik_id cascade). */
  cascadeBranchId: number | null;
  onChange: (value: string) => void;
}

export function ValueEditor({
  def,
  operator,
  value,
  options,
  cascadeBranchId,
  onChange,
}: ValueEditorProps) {
  const kind = def?.kind ?? "text";

  // Cell tree (origin_cell_id / cells): pick a cell from the lazy tree; the value
  // is the chosen cell id. The operator (eq vs child_of / includes) is chosen in
  // the operator dropdown, so the selector itself only emits the id. We always run
  // single mode here (a clause carries one cell id); "включая дочерние" is implied
  // by the child_of operator, so hide that checkbox.
  if (kind === "celltree") {
    return <CellTreeValueEditor value={value} onChange={onChange} />;
  }

  // Cascading selects (primary_id / uik_id): options come from the branch_id chosen
  // elsewhere in the form, not from /api/refs.
  if (def?.cascade) {
    return (
      <CascadeValueEditor
        cascade={def.cascade}
        branchId={cascadeBranchId}
        operator={operator}
        value={value}
        onChange={onChange}
      />
    );
  }

  // Boolean fields (is_*, verified): да / нет.
  if (kind === "bool") {
    return (
      <Select value={value || "y"} onValueChange={onChange}>
        <SelectTrigger className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="y">Да</SelectItem>
          <SelectItem value="n">Нет</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  // Dates: native date input.
  if (kind === "date") {
    return (
      <Input
        type="date"
        className="h-8"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  // Single-pick selects (branches, loyalty, membership, readiness…) — but "among"
  // wants a multi-value set, so fall through to text for comma-separated entry.
  if (
    (kind === "select" || kind === "multiselect") &&
    options.length > 0 &&
    operator !== "among" &&
    operator !== "includes"
  ) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8">
          <SelectValue placeholder="Выберите…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // "among"/multiselect with options: render a compact multi-select via chips.
  if ((kind === "select" || kind === "multiselect") && options.length > 0) {
    return <MultiSelect options={options} value={value} onChange={onChange} />;
  }

  // Numbers and free text.
  return (
    <Input
      type={kind === "number" ? "number" : "text"}
      className="h-8"
      placeholder={operator === "among" ? "значения через запятую" : "значение"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Lightweight multi-select: a list of toggleable options, value is comma-joined. */
export function MultiSelect({
  options,
  value,
  onChange,
}: {
  options: ReturnType<typeof resolveOptions>;
  value: string;
  onChange: (value: string) => void;
}) {
  const selected = React.useMemo(
    () => new Set(value.split(",").map((v) => v.trim()).filter((v) => v !== "")),
    [value],
  );

  function toggle(v: string) {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next).join(","));
  }

  return (
    <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-1.5">
      {options.map((o) => (
        <label
          key={o.value}
          className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-accent"
        >
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-primary"
            checked={selected.has(o.value)}
            onChange={() => toggle(o.value)}
          />
          <span className="truncate">{o.label}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * Cell-tree value editor for celltree fields (origin_cell_id / cells). Wraps the
 * lazy <CellTreeSelect> in single mode; the clause value is the chosen cell id
 * (kept as a string in the draft row). includeChildren is implied by the chosen
 * operator (child_of), so its checkbox is hidden.
 */
function CellTreeValueEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const ids = React.useMemo(() => {
    const n = Number(value);
    return value !== "" && Number.isFinite(n) ? [n] : [];
  }, [value]);

  return (
    <CellTreeSelect
      mode="single"
      value={ids}
      includeChildren={false}
      hideIncludeChildren
      onChange={(next) => onChange(next.length > 0 ? String(next[0]) : "")}
    />
  );
}

/**
 * Cascading select for primary_id / uik_id. Options are loaded from the branch_id
 * chosen elsewhere in the form (usePrimaries / useUiks). If no branch is selected,
 * prompts the user to choose one first.
 */
function CascadeValueEditor({
  cascade,
  branchId,
  operator,
  value,
  onChange,
}: {
  cascade: "primary" | "uik";
  branchId: number | null;
  operator: FilterOperator;
  value: string;
  onChange: (value: string) => void;
}) {
  const primaries = usePrimaries(branchId, cascade === "primary");
  const uiks = useUiks(branchId, cascade === "uik");
  const query = cascade === "primary" ? primaries : uiks;

  if (branchId == null) {
    return (
      <p className="rounded-md border border-dashed border-input px-3 py-2 text-xs text-muted-foreground">
        Сначала выберите Отделение (добавьте условие «Отделение» с одним значением).
      </p>
    );
  }

  if (query.isPending) {
    return (
      <div className="flex h-8 items-center gap-2 px-1 text-xs text-muted-foreground">
        <Spinner /> Загрузка…
      </div>
    );
  }
  if (query.isError) {
    return <p className="text-xs text-destructive">Не удалось загрузить список.</p>;
  }

  const options: FieldOption[] = (query.data ?? []).map((row) => {
    const label =
      row.title ?? row.name ?? ("number" in row && row.number != null ? `№${row.number}` : `#${row.id}`);
    return { value: String(row.id), label: String(label) };
  });

  if (options.length === 0) {
    return <p className="text-xs text-muted-foreground">Нет доступных значений.</p>;
  }

  // "among" wants a multi-value set; otherwise single select.
  if (operator === "among" || operator === "includes") {
    return <MultiSelect options={options} value={value} onChange={onChange} />;
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8">
        <SelectValue placeholder="Выберите…" />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
